import { useEffect, useState } from 'react';
import {
    BooleanInput,
    Button,
    DateTimeInput,
    FormDataConsumer,
    NumberInput,
    required,
    SelectInput,
    SimpleForm,
    TextInput,
    Toolbar,
    SaveButton,
    useNotify,
} from 'react-admin';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import FileUpload from '../../../common/inputs/FileUpload';
import VideoAutocompleteInput from '../../../common/inputs/VideoAutocompleteInput';
import AddExerciseAutoComplete from '../../../common/inputs/AddExerciseAutoComplete';
import { apiRoutes } from '../../../services/apiRoutes';
import { goApi } from '../../../services';
import { useController } from 'react-hook-form';
import AddScheduleHomeworks from './AddScheduleHomeworks';

export const SubjectAutocompleteWithScroll = ({ templateId }: { templateId: string }) => {
    const { field } = useController({ name: 'subjectId' });
    const [subjects, setSubjects] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const notify = useNotify();

    const fetchSubjects = async (pageNum: number, search: string = '') => {
        if (!templateId) return;
        setLoading(true);
        try {
            const params = new URLSearchParams({
                batchTemplateId: templateId,
                limit: '20',
                page: pageNum.toString(),
                ...(search && { name: search }),
            });
            const response = await goApi.get(
                `${apiRoutes.BATCH_TEMPLATE_SUBJECTS}?${params.toString()}`,
            );
            const newSubjects = response.data?.items || [];

            if (pageNum === 1) {
                setSubjects(newSubjects);
            } else {
                setSubjects(prev => [...prev, ...newSubjects]);
            }

            setHasMore(newSubjects.length === 20);
        } catch (err) {
            notify('Failed to fetch subjects', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setPage(1);
        setSubjects([]);
        fetchSubjects(1, searchText);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateId, searchText]);

    const handleScroll = (event: any) => {
        const listboxNode = event.currentTarget;
        const position = listboxNode.scrollTop + listboxNode.clientHeight;
        if (listboxNode.scrollHeight - position <= 1 && hasMore && !loading) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchSubjects(nextPage, searchText);
        }
    };

    return (
        <Autocomplete
            options={subjects}
            fullWidth
            getOptionLabel={(option: any) => option.name || option.title || ''}
            getOptionKey={(option: any) => option.subjectId}
            value={subjects.find((s: any) => s.subjectId === field.value) || null}
            onChange={(_, newValue) => {
                const newId = newValue?.subjectId || '';
                field.onChange(newId);
            }}
            onInputChange={(_, value, reason) => {
                if (reason === 'input') {
                    setSearchText(value);
                }
            }}
            loading={loading}
            ListboxProps={{
                onScroll: handleScroll,
                style: { maxHeight: '250px' },
            }}
            renderInput={params => (
                <TextField
                    {...params}
                    label="Subject"
                    required
                    fullWidth
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
};

export const TopicAutocompleteWithScroll = ({
    batchId,
    subjectId,
}: {
    batchId?: string;
    subjectId?: string;
}) => {
    const { field } = useController({ name: 'batchTopicId' });
    const [topics, setTopics] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const notify = useNotify();

    const fetchTopics = async (pageNum: number, search: string = '') => {
        if (!batchId) return;
        setLoading(true);
        try {
            const params = new URLSearchParams({
                batchId: batchId,
                limit: '20',
                page: pageNum.toString(),
                ...(subjectId && { subjectId }),
                ...(search && { title: search }),
            });
            const response = await goApi.get(`/batches/batch-topics?${params.toString()}`);
            const newTopics = response.data?.items || [];

            if (pageNum === 1) {
                setTopics(newTopics);
            } else {
                setTopics(prev => [...prev, ...newTopics]);
            }

            setHasMore(newTopics.length === 20);
        } catch (err) {
            notify('Failed to fetch topics', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setPage(1);
        setTopics([]);
        fetchTopics(1, searchText);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [batchId, searchText, subjectId]);

    const handleScroll = (event: any) => {
        const listboxNode = event.currentTarget;
        const position = listboxNode.scrollTop + listboxNode.clientHeight;
        if (listboxNode.scrollHeight - position <= 1 && hasMore && !loading) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchTopics(nextPage, searchText);
        }
    };

    return (
        <Autocomplete
            fullWidth
            options={topics}
            getOptionLabel={(option: any) => option?.title || ''}
            value={topics.find((t: any) => t.id === field.value) || null}
            onChange={(_, newValue) => {
                field.onChange(newValue?.id || '');
            }}
            onInputChange={(_, value, reason) => {
                if (reason === 'input') {
                    setSearchText(value);
                }
            }}
            loading={loading}
            ListboxProps={{
                onScroll: handleScroll,
                style: { maxHeight: '250px' },
            }}
            renderInput={params => (
                <TextField
                    {...params}
                    label="Topic"
                    fullWidth
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
};
export const PrimaryTeacherAutocompleteWithScroll = () => {
    const { field } = useController({ name: 'primaryTeacherId' });
    const [primaryTeachers, setPrimaryTeachers] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const notify = useNotify();

    const fetchPrimaryTeachers = async (pageNum: number, search: string = '') => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                role: 'TEACHER',
                limit: '20',
                page: pageNum.toString(),
                ...(search && { name: search }),
            });
            const response = await goApi.get(`/users?${params.toString()}`);
            const newPrimaryTeachers = response.data?.items || [];

            if (pageNum === 1) {
                setPrimaryTeachers(newPrimaryTeachers);
            } else {
                setPrimaryTeachers(prev => [...prev, ...newPrimaryTeachers]);
            }

            setHasMore(newPrimaryTeachers.length === 20);
        } catch (err) {
            notify('Failed to fetch primary teachers', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setPage(1);
        setPrimaryTeachers([]);
        fetchPrimaryTeachers(1, searchText);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchText]);

    const handleScroll = (event: any) => {
        const listboxNode = event.currentTarget;
        const position = listboxNode.scrollTop + listboxNode.clientHeight;
        if (listboxNode.scrollHeight - position <= 1 && hasMore && !loading) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchPrimaryTeachers(nextPage, searchText);
        }
    };

    return (
        <Autocomplete
            fullWidth
            options={primaryTeachers}
            getOptionLabel={(option: any) => {
                const name = `${option?.firstName || ''} ${option?.lastName || ''}`.trim();
                const phone = option?.phone || '';
                const email = option?.email;
                return `${name} | ${phone} ${email && `| ${email}`}`;
            }}
            value={primaryTeachers.find((t: any) => t.id === field.value) || null}
            onChange={(_, newValue) => {
                field.onChange(newValue?.id || '');
            }}
            onInputChange={(_, value, reason) => {
                if (reason === 'input') {
                    setSearchText(value);
                }
            }}
            loading={loading}
            ListboxProps={{
                onScroll: handleScroll,
                style: { maxHeight: '250px' },
            }}
            renderInput={params => (
                <TextField
                    {...params}
                    label="Primary Teacher"
                    fullWidth
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
};

export const ScheduleForm = ({
    batchId,
    templateId,
    schedule,
    startTime,
    endTime,
    onClose,
}: any) => {
    const notify = useNotify();
    const validateVideoOrStream = (values: any) => {
        const errors: any = {};
        const hasVideo = !!values?.videoId;
        const hasStream = !!values?.streamKey;

        if (!hasVideo && !hasStream) {
            const message = 'Select either video or stream id';
            errors.videoId = message;
            errors.streamKey = message;
        }

        if (hasVideo && hasStream) {
            const message = 'Select only one: video or stream id';
            errors.videoId = message;
            errors.streamKey = message;
        }

        return errors;
    };

    const formatDateTimeLocal = (isoString?: string) => {
        if (!isoString) return '';
        const date = new Date(isoString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    const postSave = async (data: any) => {
        const formatToISO = (dateTimeLocal: string) => {
            if (!dateTimeLocal) return '';
            const date = new Date(dateTimeLocal);
            return date.toISOString();
        };

        const payload = {
            ...data,
            batchId: batchId,
            startTime: formatToISO(data.startTime),
            endTime: formatToISO(data.endTime),
        };

        try {
            if (schedule) {
                await goApi.put(`/batches/schedules/${schedule.id}`, payload);
                notify('Schedule updated successfully', { type: 'success' });
            } else {
                await goApi.post('/batches/schedules', payload);
                notify('Schedule created successfully', { type: 'success' });
            }
            onClose?.(true);
        } catch (err: any) {
            notify(err?.message || `Failed to ${schedule ? 'update' : 'create'} schedule`, {
                type: 'error',
            });
        }
    };

    return (
        <div className="w-[500px] max-w-[90vw]">
            <p className="sticky top-0 p-2 text-lg font-semibold bg-white z-[1000] border-b border-gray-200">
                Add Schedule
            </p>
            <>
                <SimpleForm
                    onSubmit={postSave}
                    validate={validateVideoOrStream}
                    record={schedule || {}}
                    defaultValues={{
                        startTime: formatDateTimeLocal(startTime),
                        endTime: formatDateTimeLocal(endTime),
                    }}
                    toolbar={
                        <Toolbar
                            className="flex justify-between"
                            sx={{
                                position: 'sticky',
                                bottom: 0,
                                backgroundColor: 'white',
                                zIndex: 1000,
                                borderTop: '1px solid #e0e0e0',
                            }}
                        >
                            <SaveButton />
                            <Button
                                variant="outlined"
                                color="error"
                                label="Cancel"
                                onClick={() => onClose?.(false)}
                            />
                        </Toolbar>
                    }
                >
                    <FileUpload type="VIDEO_THUMBNAIL" source={'thumbnail'} accept={'image/*'} />
                    <TextInput source="title" validate={required()} fullWidth />
                    <BooleanInput source="chatEnabled" fullWidth />
                    <FormDataConsumer>
                        {({ formData }) =>
                            formData.chatEnabled && (
                                <>
                                    <SelectInput
                                        source="chatMode"
                                        defaultValue="SOCKET"
                                        fullWidth
                                        choices={[
                                            { id: 'MQTT', name: 'Old chat' },
                                            { id: 'SOCKET', name: 'New chat' },
                                        ]}
                                    />
                                </>
                            )
                        }
                    </FormDataConsumer>
                    <BooleanInput source="isDemo" fullWidth />
                    <PrimaryTeacherAutocompleteWithScroll />
                    <SubjectAutocompleteWithScroll templateId={templateId} />

                    <FormDataConsumer>
                        {({ formData }) =>
                            formData.subjectId && (
                                <TopicAutocompleteWithScroll
                                    batchId={batchId}
                                    subjectId={formData.subjectId}
                                />
                            )
                        }
                    </FormDataConsumer>
                    <FormDataConsumer>
                        {({ formData }) => (
                            <VideoAutocompleteInput
                                formData={formData}
                                fullWidth
                                source={'videoId'}
                            />
                        )}
                    </FormDataConsumer>
                    <TextInput source="streamKey" fullWidth />
                    <DateTimeInput source="startTime" validate={required()} fullWidth />
                    <DateTimeInput source="endTime" validate={required()} fullWidth />
                    <NumberInput
                        source="restrictionMaxDurationMultiple"
                        type={'number'}
                        fullWidth
                    />
                    <NumberInput source="restrictionMaxNumberPlayback" type={'number'} fullWidth />
                    {!schedule && (
                        <>
                            <FormDataConsumer>
                                {({ formData, ...rest }) =>
                                    formData.subjectId ? (
                                        <>
                                            <AddScheduleHomeworks
                                                type={'Homeworks'}
                                                fullWidth
                                                multiple={true}
                                                formData={formData}
                                                source={'homeworkIds'}
                                            />
                                        </>
                                    ) : (
                                        <div
                                            className={
                                                'text-sm w-full border border-amber-700 rounded bg-amber-100 text-amber-900 p-1'
                                            }
                                        >
                                            Select Subject for Homework
                                        </div>
                                    )
                                }
                            </FormDataConsumer>
                            <FormDataConsumer>
                                {({ formData }) => (
                                    <AddExerciseAutoComplete
                                        fullWidth
                                        formData={formData}
                                        label="Add Exercise"
                                        source={'exerciseIds'}
                                        multiple={true}
                                    />
                                )}
                            </FormDataConsumer>
                        </>
                    )}
                </SimpleForm>
            </>
        </div>
    );
};
